const ROOT_PANEL = 'root';
const DESKTOP_MEDIA_QUERY = '(min-width: 1200px)';
const DRAWER_TRANSITION_MS = 300;
const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

const initSiteNavigation = () => {
    const siteHeader = document.querySelector('.site-header');

    if (!siteHeader) {
        return;
    }

    const toggle = siteHeader.querySelector('.site-nav-toggle');
    const drawer = siteHeader.querySelector('.site-nav-drawer');
    const backdrop = siteHeader.querySelector('.site-nav-backdrop');
    const closeButton = siteHeader.querySelector('.site-nav-close');
    const backButton = siteHeader.querySelector('.site-nav-back');
    const drawerTitle = siteHeader.querySelector('.site-nav-drawer__title');
    const track = siteHeader.querySelector('.site-nav-drawer__track');
    const panels = Array.from(siteHeader.querySelectorAll('.site-nav-panel'));
    const triggerButtons = Array.from(siteHeader.querySelectorAll('.site-nav-panel__trigger'));

    if (
        !toggle ||
        !drawer ||
        !backdrop ||
        !closeButton ||
        !backButton ||
        !drawerTitle ||
        !track ||
        panels.length === 0
    ) {
        return;
    }

    const panelMap = new Map(
        panels
            .map((panel) => {
                const panelName = panel.dataset.panel;

                if (!panelName) {
                    return null;
                }

                panel.id = panel.id || `site-nav-panel-${panelName}`;

                return [panelName, panel];
            })
            .filter(Boolean),
    );
    const desktopMedia = window.matchMedia(DESKTOP_MEDIA_QUERY);
    let panelStack = [ROOT_PANEL];
    let closeResetTimeout = null;

    const syncTriggerStates = () => {
        triggerButtons.forEach((button) => {
            const targetPanel = button.dataset.target;
            const isExpanded = targetPanel ? panelStack.includes(targetPanel) : false;

            button.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');

            if (targetPanel && panelMap.has(targetPanel)) {
                button.setAttribute('aria-controls', panelMap.get(targetPanel).id);
            }
        });
    };

    const getFocusableElements = () => Array.from(drawer.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
        if (!(element instanceof HTMLElement) || element.closest('[aria-hidden="true"]')) {
            return false;
        }

        const style = window.getComputedStyle(element);
        return style.visibility !== 'hidden' && style.display !== 'none';
    });

    const getFirstFocusable = (panel) => {
        const focusable = getFocusableElements();
        return focusable.find((element) => panel.contains(element));
    };

    const setActivePanel = (panelId, shouldFocus = false) => {
        const panel = panelMap.get(panelId);

        if (!panel) {
            return;
        }

        const activeLevel = Number(panel.dataset.level || 0);

        track.style.setProperty('--site-nav-active-level', String(activeLevel));
        drawer.dataset.depth = String(activeLevel);
        drawer.dataset.activePanel = panelId;
        drawerTitle.textContent = panel.dataset.title || 'Menu';

        panels.forEach((item) => {
            const isActive = item.dataset.panel === panelId;

            item.classList.toggle('is-active', isActive);
            item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });

        syncTriggerStates();

        if (shouldFocus) {
            const firstFocusable = getFirstFocusable(panel);

            if (firstFocusable instanceof HTMLElement) {
                firstFocusable.focus();
            }
        }
    };

    const resetPanels = () => {
        panelStack = [ROOT_PANEL];
        setActivePanel(ROOT_PANEL);
    };

    const openDrawer = () => {
        window.clearTimeout(closeResetTimeout);
        resetPanels();

        siteHeader.classList.add('is-nav-open');
        document.body.classList.add('has-site-nav-open');
        toggle.setAttribute('aria-expanded', 'true');
        drawer.setAttribute('aria-hidden', 'false');

        window.requestAnimationFrame(() => {
            setActivePanel(ROOT_PANEL, true);
        });
    };

    const closeDrawer = ({ returnFocus = true, resetAfterClose = true } = {}) => {
        siteHeader.classList.remove('is-nav-open');
        document.body.classList.remove('has-site-nav-open');
        toggle.setAttribute('aria-expanded', 'false');
        drawer.setAttribute('aria-hidden', 'true');

        window.clearTimeout(closeResetTimeout);

        if (resetAfterClose) {
            closeResetTimeout = window.setTimeout(() => {
                resetPanels();
            }, DRAWER_TRANSITION_MS);
        }

        if (returnFocus) {
            toggle.focus();
        }
    };

    const forceDesktopReset = () => {
        window.clearTimeout(closeResetTimeout);
        siteHeader.classList.remove('is-nav-open');
        document.body.classList.remove('has-site-nav-open');
        toggle.setAttribute('aria-expanded', 'false');
        drawer.setAttribute('aria-hidden', 'true');
        resetPanels();
    };

    toggle.addEventListener('click', () => {
        if (siteHeader.classList.contains('is-nav-open')) {
            closeDrawer();
            return;
        }

        openDrawer();
    });

    closeButton.addEventListener('click', () => {
        closeDrawer();
    });

    backdrop.addEventListener('click', () => {
        closeDrawer();
    });

    backButton.addEventListener('click', () => {
        if (panelStack.length <= 1) {
            return;
        }

        panelStack.pop();
        setActivePanel(panelStack[panelStack.length - 1], true);
    });

    triggerButtons.forEach((button) => {
        button.addEventListener('click', () => {
            const targetPanel = button.dataset.target;

            if (!targetPanel || !panelMap.has(targetPanel)) {
                return;
            }

            panelStack.push(targetPanel);
            setActivePanel(targetPanel, true);
        });
    });

    document.addEventListener('keydown', (event) => {
        if (!siteHeader.classList.contains('is-nav-open')) {
            return;
        }

        if (event.key === 'Escape') {
            closeDrawer();
            return;
        }

        if (event.key !== 'Tab') {
            return;
        }

        const focusable = getFocusableElements();
        if (focusable.length === 0) {
            event.preventDefault();
            drawer.focus();
            return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;

        if (event.shiftKey && active === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && active === last) {
            event.preventDefault();
            first.focus();
        }
    });

    if (desktopMedia.matches) {
        forceDesktopReset();
    } else {
        resetPanels();
    }

    const handleMediaChange = (event) => {
        if (event.matches) {
            forceDesktopReset();
        }
    };

    if (typeof desktopMedia.addEventListener === 'function') {
        desktopMedia.addEventListener('change', handleMediaChange);
    } else if (typeof desktopMedia.addListener === 'function') {
        desktopMedia.addListener(handleMediaChange);
    }
};

export default initSiteNavigation;
